import { useState } from "react";
import type { Lang } from "../types.ts";
import { t } from "../i18n/ui.ts";

const DISMISSED = "t24.installHintDismissed";

/**
 * iOS never offers to install a web app — there is no `beforeinstallprompt`
 * and Safari shows no banner, so the only route is Share → Add to Home Screen.
 * Without a nudge that is invisible, hence this.
 *
 * Shown only where it applies: on iOS, in a browser tab rather than an already
 * installed copy, and only until dismissed.
 */
function shouldShow(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent;
  // Android has a real install prompt of its own, and its emulation trips the
  // iPadOS check below, so rule it out first.
  if (/Android/i.test(ua)) return false;

  // iPadOS reports itself as a Mac; the touch points give it away.
  const isIos =
    /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (!isIos) return false;

  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    // Safari's own, older flag.
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (standalone) return false;

  try {
    return localStorage.getItem(DISMISSED) !== "1";
  } catch {
    return true;
  }
}

export function InstallHint({ lang }: { lang: Lang }) {
  const [show, setShow] = useState(shouldShow);
  if (!show) return null;

  return (
    <aside className="installhint">
      <p className="installhint__title">
        <span aria-hidden="true">↑</span> {t(lang, "installTitle")}
      </p>
      <p className="installhint__body">{t(lang, "installHint")}</p>
      <button
        type="button"
        className="installhint__close"
        onClick={() => {
          try {
            localStorage.setItem(DISMISSED, "1");
          } catch {
            // Private browsing: hiding it for this visit is good enough.
          }
          setShow(false);
        }}
      >
        {t(lang, "dismiss")}
      </button>
    </aside>
  );
}
